import {
  darkenHex,
  isDarkSurface,
  lightenHex,
  parseHex,
  readableOn,
} from '../utils/color.js'
import { INTERFACE_THEME_TOKEN_CONTRACT } from './interfaceThemeContract.js'

const managedKeysByRoot = new WeakMap()
const activeOverrideByRoot = new WeakMap()
const interfaceColorTokens = new Set([
  ...Object.keys(INTERFACE_THEME_TOKEN_CONTRACT.colors.INTERFACE_OWNED),
  ...Object.keys(INTERFACE_THEME_TOKEN_CONTRACT.colors.DERIVED),
])
const interfaceVariableTokens = new Set([
  ...Object.keys(INTERFACE_THEME_TOKEN_CONTRACT.variables.INTERFACE_OWNED),
  ...Object.keys(INTERFACE_THEME_TOKEN_CONTRACT.variables.DERIVED),
])

/** Convert a six-digit hex colour into Vuetify's RGB-triplet CSS format. */
export function hexToVuetifyRgb(hex) {
  const rgb = parseHex(hex)
  return rgb ? rgb.join(',') : null
}

/** Resolve interface tokens into Vuetify's named hex-colour map. */
export function resolveInterfaceVuetifyColors(themeStyle) {
  if (!themeStyle) return {}

  const primary = themeStyle['--tw-primary']
  const accent = themeStyle['--tw-accent'] || primary
  const secondary = accent
  const background = themeStyle['--tw-background']
  const surface = themeStyle['--tw-surface']
  const text = themeStyle['--tw-text-primary'] || themeStyle['--tw-ink']
  const secondaryText = themeStyle['--tw-text-secondary'] || text
  const divider = themeStyle['--tw-divider']
  const surfaceVariant = themeStyle['--tw-surface-variant']
    || divider
    || darkenHex(surface)
  const success = themeStyle['--tw-success']
  const warning = themeStyle['--tw-warning']
  const error = themeStyle['--tw-error']
  const info = themeStyle['--tw-info']
  const onPrimary = readableOn(primary, themeStyle['--tw-on-primary-accent'], 3)
  const onAccent = readableOn(accent, themeStyle['--tw-on-accent'], 3)
  const darkSurface = isDarkSurface(surface)
  const colors = {
    primary,
    'primary-darken-1': darkenHex(primary),
    accent,
    secondary,
    'secondary-darken-1': darkenHex(secondary),
    background,
    surface,
    'surface-bright': darkSurface ? lightenHex(surface, 0.12) : surface,
    'surface-light': darkSurface ? lightenHex(surface, 0.06) : darkenHex(surface, 0.06),
    'surface-variant': surfaceVariant,
    success,
    warning,
    error,
    info,
    'on-primary': onPrimary,
    'on-accent': onAccent,
    'on-secondary': onAccent,
    'on-background': readableOn(background, text),
    'on-surface': readableOn(surface, text),
    'on-surface-variant': readableOn(surfaceVariant, secondaryText),
    'on-success': parseHex(success) ? readableOn(success) : null,
    'on-warning': parseHex(warning) ? readableOn(warning) : null,
    'on-error': parseHex(error) ? readableOn(error) : null,
    'on-info': parseHex(info) ? readableOn(info) : null,
  }

  return Object.fromEntries(
    Object.entries(colors).filter(([name, value]) => (
      interfaceColorTokens.has(name) && parseHex(value)
    )),
  )
}

/** Resolve interface-owned entries from Vuetify's theme variables map. */
export function resolveInterfaceVuetifyVariables(themeStyle) {
  if (!themeStyle) return {}
  const divider = themeStyle['--tw-divider']
    || themeStyle['--tw-surface-variant']
  const variables = parseHex(divider)
    ? { 'border-color': divider, 'border-opacity': 1 }
    : {}
  return Object.fromEntries(
    Object.entries(variables).filter(([name]) => interfaceVariableTokens.has(name)),
  )
}

/**
 * Resolve the interface's existing --tw-* palette into Vuetify theme tokens.
 * Interface identity owns the brand palette; readable on-colours are paired
 * with the resolved light/dark surfaces selected by useInterfaceTheme.
 */
export function resolveInterfaceVuetifyTheme(themeStyle) {
  const resolved = {}
  for (const [name, value] of Object.entries(resolveInterfaceVuetifyColors(themeStyle))) {
    const rgb = hexToVuetifyRgb(value)
    if (rgb) resolved[`--v-theme-${name}`] = rgb
  }
  for (const [name, value] of Object.entries(resolveInterfaceVuetifyVariables(themeStyle))) {
    if (name === 'border-color') {
      const rgb = hexToVuetifyRgb(value)
      if (rgb) resolved[`--v-${name}`] = rgb
    } else {
      resolved[`--v-${name}`] = String(value)
    }
  }
  return resolved
}

/**
 * Restore every Vuetify theme scope touched by the active interface without
 * deactivating it. Branding calls this before rebuilding its base colors.
 */
export function restoreActiveInterfaceVuetifyThemeBase(root, vuetifyInstance = null) {
  const active = root && activeOverrideByRoot.get(root)
  const themeProxy = vuetifyInstance?.theme || vuetifyInstance
  const themesRef = themeProxy?.themes
  if (!active || !themesRef?.value || !active.baseThemes?.size) return

  const restoredThemes = { ...themesRef.value }
  for (const [name, theme] of active.baseThemes) {
    restoredThemes[name] = {
      ...theme,
      colors: { ...(theme.colors || {}) },
      variables: { ...(theme.variables || {}) },
    }
  }
  themesRef.value = restoredThemes
}

/**
 * Reassert the active interface palette after a base branding pass.
 * Branding owns the domain/personal layer; this registry owns only the final
 * interface override and is populated solely by the technician shell lifecycle.
 */
export function reapplyActiveInterfaceVuetifyTheme(root, vuetifyInstance = null) {
  if (!root) return
  const active = activeOverrideByRoot.get(root)
  if (!active) return
  for (const [key, value] of Object.entries(active.rootVars)) {
    root.style.setProperty(key, value)
  }

  const themeProxy = vuetifyInstance?.theme || vuetifyInstance
  const themesRef = themeProxy?.themes
  if (!themesRef?.value) return
  const themeName = active.themeName || themeProxy.global?.name?.value
  if (!themeName) return
  const current = themesRef.value[themeName] || {
    dark: themeName === 'dark',
    colors: {},
  }
  active.baseThemes.set(themeName, {
    ...current,
    colors: { ...(current.colors || {}) },
    variables: { ...(current.variables || {}) },
  })
  themesRef.value = {
    ...themesRef.value,
    [themeName]: {
      ...current,
      colors: { ...(current.colors || {}), ...active.colors },
      variables: { ...(current.variables || {}), ...active.variables },
    },
  }
}

/**
 * Apply or clear the global interface palette. Root variables cover plain
 * descendants; updating Vuetify's theme map covers `.v-theme--light` /
 * `.v-theme--dark` scopes and teleported overlays that override inheritance.
 * Clearing delegates restoration to the branding store so domain/personal
 * precedence remains defined in one place.
 */
export function applyInterfaceVuetifyThemeToRoot(
  root,
  themeStyle,
  active,
  restoreBranding,
  vuetifyInstance = null,
  themeName = null,
) {
  if (!root) return
  const previous = managedKeysByRoot.get(root) || new Set()
  const previousActive = activeOverrideByRoot.get(root)

  for (const key of previous) root.style.removeProperty(key)
  restoreActiveInterfaceVuetifyThemeBase(root, vuetifyInstance)
  activeOverrideByRoot.delete(root)

  if (!active) {
    managedKeysByRoot.delete(root)
    if (previous.size && typeof restoreBranding === 'function') restoreBranding()
    return
  }

  if (typeof restoreBranding === 'function') restoreBranding()

  const rootVars = resolveInterfaceVuetifyTheme(themeStyle)
  const next = new Set(Object.keys(rootVars))
  const themeProxy = vuetifyInstance?.theme || vuetifyInstance
  activeOverrideByRoot.set(root, {
    rootVars,
    colors: resolveInterfaceVuetifyColors(themeStyle),
    variables: resolveInterfaceVuetifyVariables(themeStyle),
    themeName: themeName || themeProxy?.global?.name?.value || 'light',
    baseThemes: previousActive?.baseThemes || new Map(),
  })
  managedKeysByRoot.set(root, next)

  // The base layer was restored before registration, so omitted interface
  // tokens fall back to current branding instead of leaking the old preset.
  reapplyActiveInterfaceVuetifyTheme(root, vuetifyInstance)
}
